import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CountdownCalculator = () => {
  const [targetDate, setTargetDate] = useState('');
  const [timeLeft, setTimeLeft] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [solutionSteps, setSolutionSteps] = useState([]);

  const handleTargetDateChange = (e) => {
    setTargetDate(e.target.value);
    setError('');
  };

  const startCountdown = () => {
    if (!targetDate) {
      setError('Please enter a target date and time.');
      return;
    }

    const target = new Date(targetDate);
    if (isNaN(target.getTime()) || target <= new Date()) {
      setError('Please enter a valid future date and time.');
      return;
    }

    setTimeLeft(target.getTime() - new Date().getTime());
  };

  useEffect(() => {
    let interval = null;

    if (timeLeft !== null) {
      interval = setInterval(() => {
        const now = new Date().getTime();
        const distance = timeLeft - now;

        if (distance < 0) {
          clearInterval(interval);
          setTimeLeft(null);
          return;
        }

        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        setChartData({
          labels: ['Days', 'Hours', 'Minutes', 'Seconds'],
          datasets: [
            {
              label: 'Countdown',
              data: [days, hours, minutes, seconds],
              backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)', 'rgba(255, 206, 86, 0.5)', 'rgba(153, 102, 255, 0.5)'],
              borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)', 'rgba(255, 206, 86, 1)', 'rgba(153, 102, 255, 1)'],
              borderWidth: 1
            }
          ]
        });

        setSolutionSteps([
          `Target Date: ${targetDate}`,
          `Current Time: ${new Date().toLocaleString()}`,
          `Time Left: ${days} days, ${hours} hours, ${minutes} minutes, ${seconds} seconds`
        ]);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [timeLeft, targetDate]);

  return (
    <>
      <Helmet>
        <title>Countdown Calculator | BioLab Tools</title>
        <meta name="description" content="Set a specific date and time for a countdown and track the remaining time in days, hours, minutes, and seconds." />
        <meta name="keywords" content="countdown calculator, date countdown, time calculator" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">⏳</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Countdown Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Set a specific date and time for a countdown and track the remaining time in days, hours, minutes, and seconds.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Date and Time</label>
                <input
                  type="datetime-local"
                  value={targetDate}
                  onChange={handleTargetDateChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <button
                type="button"
                onClick={startCountdown}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-700 transition duration-200"
              >
                Start Countdown
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {timeLeft !== null && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Countdown</h2>
                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <p className="text-lg font-medium">Time Left: {solutionSteps[2]}</p>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Value' }
                            },
                            x: { title: { display: true, text: 'Category' } }
                          },
                          plugins: { title: { display: true, text: 'Countdown Timer' } }
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Solution Steps</h3>
                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <ul className="list-disc pl-6 space-y-2">
                        {solutionSteps.map((step, index) => (
                          <li key={index} className="text-gray-600">{step}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions (FAQ)</h2>
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is a countdown calculator?</h3>
                <p className="text-gray-600">
                  A countdown calculator allows you to set a specific date and time and tracks the remaining time until that moment.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How does the countdown work?</h3>
                <p className="text-gray-600">
                  The countdown calculates the difference between the current time and the target time, updating every second.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use this countdown for events?</h3>
                <p className="text-gray-600">
                  Yes, this countdown is perfect for tracking the time remaining until events, launches, or any future date.
                </p>
              </div>
            </div>
          </div>

          {/* How to Use Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use the Countdown Calculator</h2>
            <div className="space-y-8">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Target Date and Time</h3>
                <p className="text-gray-600">
                  Input the target date and time for the countdown.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Start Countdown</h3>
                <p className="text-gray-600">
                  Click the "Start Countdown" button to begin the countdown.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: View Countdown and Visualization</h3>
                <p className="text-gray-600">
                  The countdown will display the remaining time, along with a bar chart visualizing the countdown.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CountdownCalculator;